import { useEffect, useState } from 'react';
import { redirect } from 'next/navigation';
import { otpFrom } from '@/utils/constants';
import { useGetErrorMessage } from '@/utils/dbMessagesHelper';
import { decrypt } from '@/utils/decrypt';
import { hashPassword } from '@/utils/hashPassword';
import redirectURLChecker from '@/utils/redirectURLChecker';
import { getCookie } from 'cookies-next';
import { toast } from 'sonner';

import { email_template_config } from '@/config/email_template.config';
import postMessageToParent from '@/lib/postMessage';
import useLoginWithPassword from '@/hooks/useLoginWithPassword';
import useOtpSend from '@/hooks/useOtpSend';
import useOtpVerify from '@/hooks/useOtpVerify';
import useSessionStorageInfo from '@/hooks/useSessionStorageInfo';
import { useTranslations } from '@/hooks/useTranslations';
import LanguageConflictHelper from '@/components/reUsables/LanguageConflictHelper';

import OTPForm from './OTPForm';
import PasswordForm from './PasswordForm';

const PasswordAndOtpForm = ({
  email,
  setIsEmailForm,
  signUpWith,
  tenantId,
  isPasswordEnabled,
  isOtpEnable,
  shakey,
}: {
  email: string;
  setIsEmailForm: (isEmailForm: boolean) => void;
  signUpWith: string;
  isOtpEnable: boolean;
  tenantId: string;
  isPasswordEnabled: boolean | undefined;
  shakey: string;
}) => {
  const { translations, isLoading: isTranslationsLoading } =
    useTranslations('auth_page');

  const { getDBMessage } = useGetErrorMessage();

  const password = translations?.password || 'Password';
  const otp = translations?.otp || 'OTP';
  const otp_sending = translations?.otp_sending || 'OTP Sending...';
  const select_login_method =
    translations?.select_login_method || 'Select your login method';

  // Error and success message translations
  const login_success =
    translations?.success_messages?.login_success || 'Successfully logged in.';
  const otp_sending_error =
    translations?.error_messages?.otp_sending_error ||
    'Error while sending OTP';

  const { redirecturl, postParent, app_id } = useSessionStorageInfo();
  const [passwordValue, setPassword] = useState('');
  const [isPasswordSelected, setIsPasswordSelected] = useState(false);
  const { tenantData, clientId } = useSessionStorageInfo();
  const [otpValue, setOtp] = useState('');
  const [passwordErroMsg, setPasswordErrorMsg] = useState('');
  const [otpErroMsg, setOtpErrorMsg] = useState('');
  const encodedParamsStringify = getCookie('encodedParams') || '{}';
  const encodedParams = JSON.parse(encodedParamsStringify);
  const locale = getCookie('NEXT_LOCALE');
  const [updateURL, setUpdateURL] = useState<URL | null>(null);
  const [newParams, setNewParams] = useState<any>({});

  const {
    loading: passwordLoading,
    data: passwordData,
    handleClick: handleLoginWithPassword,
    error: passwordError,
  } = useLoginWithPassword();

  const {
    loading: otpSendLoading,
    data: sendOtpResponse,
    handleClick: sendOtp,
    error: otpSendError,
  } = useOtpSend(email_template_config.SIGNIN_OTP_TEMPLATE);

  const {
    loading: otpLoading,
    data: otpVerifyResponse,
    handleClick: handleOtpVerify,
    error: otpVerifyError,
  } = useOtpVerify();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newPassword = decrypt(shakey) + passwordValue;
    const hashPass = hashPassword(newPassword);
    handleLoginWithPassword(email, tenantId, hashPass, app_id);
  };

  const handleVerify = () => {
    const secondary_user_id = null;
    handleOtpVerify(
      email,
      tenantId,
      otpValue,
      secondary_user_id,
      false, // isCheckedNewsletter
      app_id
    );
  };

  useEffect(() => {
    const response: any = passwordData;
    if (response && response.accessToken) {
      toast.success(login_success);
      setOtpErrorMsg('');
      setPasswordErrorMsg('');
      if (postParent) {
        postMessageToParent({ ...response, client_id: clientId });
        redirect('/successful-login');
      }
      if (redirecturl) {
        const newParams = {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          tenantId: tenantData?._id,
          tenantName: tenantData?.tenantName,
          clientId: clientId,
          locale: locale as string,
        };
        const updatedURL = redirectURLChecker(redirecturl, {
          ...newParams,
          ...encodedParams,
          locale: locale as string,
        });
        setUpdateURL(updatedURL);
        setNewParams(newParams);
      }
    }
  }, [passwordData, login_success]);

  useEffect(() => {
    if (otpVerifyResponse) {
      if (otpVerifyResponse.success) {
        if (postParent) {
          postMessageToParent({ ...otpVerifyResponse, client_id: clientId });
          redirect('/successful-login');
        }

        if (redirecturl) {
          const newParams = {
            accessToken: otpVerifyResponse.accessToken,
            refreshToken: otpVerifyResponse.refreshToken,
            tenantId: tenantData?._id,
            tenantName: tenantData?.tenantName,
            clientId: clientId,
            locale: locale as string,
          };

          const updatedURL = redirectURLChecker(redirecturl, {
            ...newParams,
            ...encodedParams,
            locale: locale as string,
          });

          setUpdateURL(updatedURL);
          setNewParams(newParams);
        }
      } else {
        toast.error(otpVerifyResponse.message);
        setOtpErrorMsg(otpVerifyResponse.message);
      }
    }
  }, [otpVerifyResponse]);

  useEffect(() => {
    if (passwordError) {
      toast.error(getDBMessage(passwordError) || passwordError);
      setPasswordErrorMsg(getDBMessage(passwordError) || passwordError);
    }

    if (otpVerifyError) {
      toast.error(getDBMessage(otpVerifyError) || otpVerifyError);
      setOtpErrorMsg(getDBMessage(otpVerifyError) || otpVerifyError);
    }
  }, [passwordError, otpVerifyError]);

  useEffect(() => {
    if (isPasswordEnabled === true && signUpWith === 'linkedin') {
      setIsPasswordSelected(true);
    } else if (isPasswordEnabled === true) {
      setIsPasswordSelected(true);
    } else if (signUpWith === 'otp') {
      setIsPasswordSelected(false);
    }
  }, [signUpWith]);

  useEffect(() => {
    if (sendOtpResponse) {
      setIsPasswordSelected(false);
    }
  }, [sendOtpResponse]);

  useEffect(() => {
    if (otpSendError) {
      toast.error(otp_sending_error);
      setOtpErrorMsg(otp_sending_error);
      setIsPasswordSelected(true);
    }
  }, [otpSendError, otp_sending_error]);

  if (isTranslationsLoading) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
          <div className="h-9 w-full animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {isPasswordEnabled && isOtpEnable && (
        <div>
          <p className="mb-2 text-sm text-zinc-500">{select_login_method}</p>
          <div className="grid h-[36px] grid-cols-2 items-center justify-center gap-x-3 bg-zinc-100 px-1 text-sm">
            <div
              onClick={() => setIsPasswordSelected(true)}
              className={`flex h-[28px] cursor-pointer items-center justify-center ${isPasswordSelected ? 'bg-white font-medium' : 'bg-transparent'}`}
            >
              {password}
            </div>
            <div
              onClick={() => {
                if (isPasswordSelected) {
                  sendOtp(email, tenantId, otpFrom.SIGNIN);
                }
              }}
              className={`flex h-[28px] ${otpSendLoading ? 'cursor-wait' : 'cursor-pointer'} items-center justify-center ${!isPasswordSelected ? 'bg-white font-medium' : 'bg-transparent'}`}
            >
              {otpSendLoading ? otp_sending : otp}
            </div>
          </div>
        </div>
      )}
      {isPasswordSelected ? (
        <PasswordForm
          email={email}
          loading={passwordLoading}
          password={passwordValue}
          setPassword={setPassword}
          setIsEmailForm={setIsEmailForm}
          handleLogin={handleLogin}
          tenantId={tenantId}
          errorMsg={passwordErroMsg}
        />
      ) : (
        <OTPForm
          loading={otpLoading}
          email={email}
          value={otpValue}
          setValue={setOtp}
          handleVerify={handleVerify}
          setIsEmailForm={setIsEmailForm}
          tenantId={tenantId}
          errorMsg={otpErroMsg}
          setErrorMsg={setOtpErrorMsg}
        />
      )}

      {updateURL && (
        <LanguageConflictHelper updateURL={updateURL} newParams={newParams} />
      )}
    </div>
  );
};

export default PasswordAndOtpForm;
